import { uid } from 'radash';

import { CELL_HEIGHT, CELL_PADDING, CELL_WIDTH, ICON_HEIGHT, ICON_WIDTH } from '@diagram/diagram-dimensions';
import { ElementHelpers } from '@diagram/helpers/ElementHelpers';
import { DiagramSyncService } from '@diagram/services/DiagramSyncService';
import { ViewportService } from '@diagram/services/ViewportService';
import { derivedDiagramState, diagram } from '@diagram/state/diagram';

import type { DiagramGroup, Point } from 'models';
import { DiagramGroupSchema, PositionAndDimensions } from 'models';

import { InternalTrackingService } from 'services/InternalTrackingService';

const GroupService = {
    ...ElementHelpers('group'),

    create: (position: Point, width: number, height: number) => {
        const id = uid(24);
        const newGroup = { position, width, height, id };

        const validatedGroup = DiagramGroupSchema.parse(newGroup);

        InternalTrackingService.track(`groups.create`, {
            group: validatedGroup,
        });

        diagram.groups.push(validatedGroup);
    },

    addExisting: (data: DiagramGroup) => {
        const id = uid(24);
        const newGroup = { ...data, id };

        const validatedGroup = DiagramGroupSchema.parse(newGroup);

        InternalTrackingService.track(`groups.create`, {
            group: validatedGroup,
        });

        diagram.groups.push(validatedGroup);
    },

    update: (id: string, data: Partial<DiagramGroup>) => {
        const { element: current } = GroupService.getById(id);
        if (!current) {
            throw new Error('Current group not found.');
        }

        Object.keys(data).forEach((key) => {
            DiagramSyncService.updateByKey(`groups.${id}.${key}`, data[key as keyof DiagramGroup], true);
        });
    },

    updatePositionAndSize: (id: string, unsafePosition: PositionAndDimensions) => {
        const { element: current } = GroupService.getById(id);
        if (!current) {
            throw new Error('Current group not found.');
        }

        const position = GroupService.getSafePosition(unsafePosition);

        if (position.x !== current.position.x) {
            DiagramSyncService.updateByKey(`groups.${id}.position.x`, position.x, true);
        }

        if (position.y !== current.position.y) {
            DiagramSyncService.updateByKey(`groups.${id}.position.y`, position.y, true);
        }

        // @ts-ignore refactor this to dimensions field
        if (position.width !== current.width) {
            DiagramSyncService.updateByKey(`groups.${id}.width`, position.width, true);
        }

        // @ts-ignore refactor this to dimensions field
        if (position.height !== current.height) {
            DiagramSyncService.updateByKey(`groups.${id}.height`, position.height, true);
        }
    },

    getSafePosition: (unsafePosition: PositionAndDimensions) => ({
        ...(unsafePosition.width > 0
            ? { x: unsafePosition.x, width: unsafePosition.width }
            : { x: unsafePosition.x + unsafePosition.width, width: unsafePosition.width * -1 }),
        ...(unsafePosition.height > 0
            ? { y: unsafePosition.y, height: unsafePosition.height }
            : { y: unsafePosition.y + unsafePosition.height, height: unsafePosition.height * -1 }),
    }),

    getComponentInstancesInsideGroup: (groupId: string) => {
        const group = GroupService.getById(groupId).element as unknown as DiagramGroup;

        if (!group) return [];

        return derivedDiagramState.componentInstancesAsArray.filter((componentInstance) => {
            const instancePosition = componentInstance.position;
            const groupPosition = group.position;

            const upperLeftCornerX = CELL_WIDTH * instancePosition.x + CELL_WIDTH - ICON_WIDTH - CELL_PADDING;
            const upperLeftCornerY = CELL_HEIGHT * instancePosition.y + CELL_HEIGHT - ICON_HEIGHT - CELL_PADDING;
            const lowerRightCornerX = upperLeftCornerX + ICON_WIDTH + (componentInstance.colSpan - 1) * CELL_WIDTH;
            const lowerRightCornerY = upperLeftCornerY + ICON_HEIGHT + (componentInstance.rowSpan - 1) * CELL_HEIGHT;

            return (
                upperLeftCornerX >= groupPosition.x &&
                lowerRightCornerX <= groupPosition.x + group.width &&
                upperLeftCornerY >= groupPosition.y &&
                lowerRightCornerY <= groupPosition.y + group.height
            );
        });
    },

    moveCenterIntoView: (groupId: string) => {
        const group = GroupService.getById(groupId).element as unknown as DiagramGroup;

        if (!group) return;

        const center = {
            x: group.position.x + group.width / 2,
            y: group.position.y + group.height / 2,
        };

        ViewportService.moveIntoView(center);
    },
};

export { GroupService };
